import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScoreBar } from './ScoreBar';
import { CategoryIndicators } from './CategoryIndicators';
import { BrandCard } from './BrandCard';
import type { BrandOption } from '../completeness.api';
import { SkeletonTable } from '@/components/feedback/SkeletonCard';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';

interface BrandGridProps {
  brands: BrandOption[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
  layout: 'grid' | 'list';
  onBrandClick?: (brand: BrandOption) => void;
}

function BrandListLogo({ brand }: { brand: BrandOption }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div
      className="flex items-center justify-center rounded-lg"
      style={{
        width: 40,
        height: 40,
        backgroundColor: 'var(--primary-faint)',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {brand.logoUrl && !imgError ? (
        <img
          src={brand.logoUrl}
          alt={brand.name}
          style={{ width: 30, height: 30, objectFit: 'contain' }}
          onError={() => setImgError(true)}
        />
      ) : (
        <span
          className="text-[0.625rem] font-bold uppercase"
          style={{ color: 'var(--primary)', letterSpacing: '0.02em' }}
        >
          {brand.name.slice(0, 2)}
        </span>
      )}
    </div>
  );
}

export function BrandGrid({
  brands,
  isLoading,
  isError,
  error,
  onRetry,
  layout,
  onBrandClick,
}: BrandGridProps) {
  const { t } = useTranslation();

  if (isLoading) return <SkeletonTable rows={6} />;
  if (isError) return <ApiErrorMessage error={error} onRetry={onRetry} />;
  if (!brands || brands.length === 0) {
    return (
      <div className="flex flex-col items-center py-10">
        <span
          className="material-symbols-outlined mb-2"
          style={{ fontSize: 32, color: 'var(--foreground-muted)', opacity: 0.5 }}
        >
          inventory_2
        </span>
        <span className="text-sm" style={{ color: 'var(--foreground-muted)' }}>
          {t('common.noResults')}
        </span>
      </div>
    );
  }

  if (layout === 'grid') {
    return (
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}
      >
        {brands.map((brand) => (
          <BrandCard key={brand.id} brand={brand} onClick={onBrandClick} />
        ))}
      </div>
    );
  }

  // List layout
  return (
    <div
      className="overflow-hidden rounded-xl"
      style={{
        border: '1px solid var(--border-soft)',
        backgroundColor: 'var(--background)',
      }}
    >
      {brands.map((brand, idx) => (
        <div
          key={brand.id}
          className="flex cursor-pointer items-center gap-4 px-5 py-3.5 transition-all duration-100"
          style={{
            borderBottom: idx < brands.length - 1 ? '1px solid var(--border-soft)' : 'none',
          }}
          onClick={() => onBrandClick?.(brand)}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--primary-faint)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '';
          }}
        >
          <BrandListLogo brand={brand} />
          <div
            className="min-w-[120px] text-[0.8125rem] font-semibold"
            style={{ color: 'var(--foreground)' }}
          >
            {brand.name}
          </div>
          <div className="flex flex-1 items-center gap-6">
            <span
              className="min-w-[110px] text-xs"
              style={{ color: 'var(--foreground-muted)' }}
            >
              <strong style={{ fontWeight: 600, color: 'var(--foreground)' }}>
                {brand.productCount}
              </strong>{' '}
              {t('products.brandProducts')}
            </span>
            <CategoryIndicators categories={brand.categoryScores} />
            <div style={{ maxWidth: 200, flex: 1 }}>
              <ScoreBar score={brand.score} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
